import styles from "./EditUser.module.css";
import { User } from "../../types/userType";
import React, { useState } from "react";
import config from "../../config";
import { useUser } from "../../contexts/UserContext";
import normalizePhoneNumber from "../../utils/normalizePhoneNumber";
import CreateFuelCodeModal from "./CreateFuelCodeModal";

interface EditUserProps {
  user: User;
  open: boolean;
  setOpen: (open: boolean) => void;
  onChatClosed?: () => void;
  setIsActiveChat: (active: boolean) => void;
  handleUserSelect: (userId: string) => void;
  selectedUserId: string | null;
  setSelectedUserId: (id: string | null) => void;
  onFuelCodeGenerated?: (code: string, codeValue: string) => void;
  onSendMessage?: (message: string) => void;
}

// Inline confirmation dialog component
type ConfirmDialogProps = {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  message: string;
};
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  message,
}) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: 24,
          borderRadius: 8,
          minWidth: 300,
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: 16 }}>{message}</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
          <button
            onClick={onConfirm}
            style={{ marginRight: 8 }}
            className={styles.confirmButton}
          >
            אישור
          </button>
          <button onClick={onCancel} className={styles.cancelButton}>
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
};

const EditUser = ({
  user,
  open,
  setOpen,
  onChatClosed,
  setIsActiveChat,
  handleUserSelect,
  selectedUserId,
  setSelectedUserId,
  onFuelCodeGenerated,
  onSendMessage,
}: EditUserProps) => {
  const {
    usersWithActiveChat,
    setUsersWithActiveChat,
    usersWithChatHistory,
    setUsersWithChatHistory,
  } = useUser();
  const [isClosing, setIsClosing] = useState(false);
  const [editName, setEditName] = useState(user.name || "");
  const [editNameError, setEditNameError] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [isClearingMessages, setIsClearingMessages] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isCreateCodeModalOpen, setIsCreateCodeModalOpen] = useState(false);
  const [updatingChatType, setUpdatingChatType] = useState(false);
  const [chatTypeError, setChatTypeError] = useState("");
  const [createCodeLoading, setCreateCodeLoading] = useState(false);
  const [createCodeError, setCreateCodeError] = useState("");
  const [createCodeResult, setCreateCodeResult] = useState<null | {
    codeValue?: string | number;
    message: string;
  }>(null);
  const [formData, setFormData] = useState({
    carNumber: "",
    fullName: "",
    idNumber: "",
    altCarNumber: "",
    reason: "",
  });

  const handleClearMessages = () => {
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    await clearUserMessages();
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  const closeChat = async () => {
    if (!user._id) return;
    try {
      setIsClosing(true);
      const res = await fetch(`${config.backendUrl}/users/close-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ userId: user._id }),
      });
      if (res.ok) {
        //update usersWithActiveChat and usersWithChatHistory to conversationState: "null"
        setUsersWithActiveChat(
          usersWithActiveChat.map((user) => ({
            ...user,
            conversationState: undefined,
          }))
        );
        setUsersWithChatHistory(
          usersWithChatHistory.map((user) => ({
            ...user,
            conversationState: undefined,
          }))
        );
        setOpen(false);
        if (onChatClosed) onChatClosed();
        setIsClosing(false);
      } else {
        console.log("Failed to close chat");
        setIsClosing(false);
      }
    } catch (e) {
      console.log("Error closing chat");
      setIsClosing(false);
    }
  };

  const openChat = async () => {
    if (!user._id) return;
    try {
      setIsClosing(true);
      const res = await fetch(
        `${config.backendUrl}/users/update-conversation-state`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            userId: user._id,
            conversationState: "awaiting_human_chat",
          }),
        }
      );
      setIsClosing(false);
      setIsActiveChat(true);
      //update usersWithActiveChat and usersWithChatHistory to conversationState: "awaiting_human_chat"
      setUsersWithActiveChat(
        usersWithActiveChat.map((user) => ({
          ...user,
          conversationState: "awaiting_human_chat",
        }))
      );
      setUsersWithChatHistory(
        usersWithChatHistory.map((user) => ({
          ...user,
          conversationState: "awaiting_human_chat",
        }))
      );
      if (user._id) {
        setTimeout(() => {
          handleUserSelect(user._id as string);
        }, 0);
        setTimeout(() => {
          onSendMessage?.("שלום, נפתחה לך שיחה עם נציג");
        }, 1000);
      }
    } catch (e) {
      console.log("Error opening chat");
      setIsClosing(false);
    }
  };

  const clearUserMessages = async () => {
    if (!user._id) return;
    try {
      setIsClearingMessages(true);
      const res = await fetch(
        `${config.backendUrl}/users/clear-user-messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ userId: user._id }),
        }
      );
      if (res.ok) {
        // Update local state for messages
        setUsersWithActiveChat(
          usersWithActiveChat.map((u) =>
            u._id === user._id ? { ...u, messages: [] } : u
          )
        );
        setUsersWithChatHistory(
          usersWithChatHistory.map((u) =>
            u._id === user._id ? { ...u, messages: [] } : u
          )
        );
        // Deselect the user if their messages were cleared
        if (selectedUserId === user._id) {
          setSelectedUserId(null);
        }
        setIsClearingMessages(false);
      } else {
        console.log("Failed to clear user messages");
        setIsClearingMessages(false);
      }
    } catch (e) {
      console.log("Error clearing user messages");
      setIsClearingMessages(false);
    }
  };

  const saveName = async () => {
    if (!user._id) return;
    setEditNameError("");
    setSavingName(true);

    try {
      const res = await fetch(`${config.backendUrl}/users/update-user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          userId: user._id,
          name: editName,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        // Update local state for name
        setUsersWithActiveChat(
          usersWithActiveChat.map((u) =>
            u._id === user._id ? { ...u, name: editName } : u
          )
        );
        setUsersWithChatHistory(
          usersWithChatHistory.map((u) =>
            u._id === user._id ? { ...u, name: editName } : u
          )
        );
      } else {
        setEditNameError(data.message || "שגיאה בעדכון השם");
      }
    } catch (e) {
      setEditNameError("שגיאה בעדכון השם");
    }
    setSavingName(false);
  };

  const handleChatTypeChange = async (newType: "attached_general" | "pool_rental") => {
    if (!user._id) return;
    setUpdatingChatType(true);
    setChatTypeError("");

    try {
      const res = await fetch(`${config.backendUrl}/users/update-user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          userId: user._id,
          representativeChatType: newType,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        // Update local state for representativeChatType
        setUsersWithActiveChat(
          usersWithActiveChat.map((u) =>
            u._id === user._id ? { ...u, representativeChatType: newType } : u
          )
        );
        setUsersWithChatHistory(
          usersWithChatHistory.map((u) =>
            u._id === user._id ? { ...u, representativeChatType: newType } : u
          )
        );
      } else {
        setChatTypeError(data.message || "שגיאה בעדכון נושא השיחה");
      }
    } catch (e) {
      setChatTypeError("שגיאה בעדכון נושא השיחה");
    }
    setUpdatingChatType(false);
  };

  const handleCreateCodeClick = () => {
    setIsCreateCodeModalOpen(true);
    setCreateCodeError("");
    setCreateCodeResult(null);
  };

  const handleCreateCodeCancel = () => {
    setIsCreateCodeModalOpen(false);
    setCreateCodeError("");
    setCreateCodeResult(null);
  };

  const handleCreateCodeSubmit = async (
    data: {
      carNumber: string;
      fullName: string;
      idNumber: string;
      altCarNumber: string;
      reason: string;
    },
    e?: React.FormEvent
  ) => {
    if (e) e.preventDefault();
    setCreateCodeLoading(true);
    setCreateCodeError("");
    setCreateCodeResult(null);
    try {
      const res = await fetch(`${config.backendUrl}/users/generate-fuel-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          userId: user._id,
          ...data,
        }),
      });
      const result = await res.json();
      if (res.ok) {
        setCreateCodeResult({
          codeValue: result.codeValue,
          message: result.message || "מספר תדלוק הופק ונשלח ב-WhatsApp",
        });
        const amount =
          result.codeValue !== undefined && result.codeValue !== null
            ? ` (סכום: ${result.codeValue} ש"ח)`
            : "";
        window.alert(
          (result.message || "מספר תדלוק הופק ונשלח ב-WhatsApp") + amount
        );
        setIsCreateCodeModalOpen(false);
      } else {
        setCreateCodeError(result.message || "שגיאה בהפקת מספר תדלוק");
      }
    } catch (e) {
      setCreateCodeError("שגיאה בהפקת מספר תדלוק");
    }
    setCreateCodeLoading(false);
  };

  const lastMessageReceivedDate = user.closedChatAt?.toLocaleString();
  const lastMessageReceivedDateString = lastMessageReceivedDate
    ? new Date(lastMessageReceivedDate).toLocaleDateString("he-IL", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    })
    : "";

  return (
    <div
      className={styles.editUserContainer}
      style={{
        height: open ? "240px" : "0px",
        overflow: "hidden",
        transition: "height 0.3s",
        boxShadow: open ? "0 5px 10px 0 rgba(0, 0, 0, 0.1)" : "none",
      }}
    >
      {/* Chat type select */}
      <div className={styles.chatTypeSelectContainer}>
        <div style={{ fontSize: 16, color: "#888", marginBottom: 4 }}>נושא שיחה:</div>
        <select
          value={user.representativeChatType || ""}
          onChange={(e) => {
            const newType = e.target.value as "attached_general" | "pool_rental";
            if (newType) {
              handleChatTypeChange(newType);
            }
          }}
          disabled={updatingChatType}
          className={styles.chatTypeSelect}
          style={{
            width: "140px",
            padding: "3px",
            fontSize: "12px",
            border: "1px solid var(--gray-color)",
            borderRadius: "4px",
            backgroundColor: "white",
            cursor: updatingChatType ? "not-allowed" : "pointer",
            opacity: updatingChatType ? 0.6 : 1,
          }}
        >
          <option value="attached_general">צמוד/כללי</option>
          <option value="pool_rental">מאגר/שכור</option>
        </select>
      </div>
      <div className={styles.editItemContainer}>
        <div className={styles.editUserHeader}>
          {/* Open/Close chat button */}
          {user.conversationState === "awaiting_human_chat" ? (
            <div onClick={closeChat} className={styles.openCloseButton}>
              סגירת שיחה
            </div>
          ) : (
            <div style={{ color: "red", fontSize: 14 }}>
              {lastMessageReceivedDateString &&
                `שיחה נסגרה ב: ${lastMessageReceivedDateString}`}
            </div>
            // <div className={styles.openCloseButton} onClick={openChat}>
            //   פתח שיחה
            // </div>
          )}

          {/* User name and phone display */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 16, color: "#888" }}>
              {normalizePhoneNumber(user.phone)}
            </span>
          </div>
        </div>
      </div>
      {/* Edit name input */}
      <div className={styles.editItemContainer}>
        <div className={styles.editUserInputContainer}>
          <input
            type="text"
            value={editName === user.phone ? "" : editName}
            onChange={(e) => setEditName(e.target.value)}
            className={styles.editUserInput}
            placeholder="עדכן שם משתמש"
            disabled={savingName}
          />
          <button
            onClick={saveName}
            disabled={savingName || !editName.trim() || editName === user.name}
            className={styles.editUserButton}
          >
            עדכון
          </button>
        </div>
      </div>

      {/* //create full code and send it to the user */}
      <div className={styles.editItemContainer}>
        <button
          className={styles.createCodeButton}
          onClick={handleCreateCodeClick}
        >
          הפקת מספר תדלוק
        </button>
      </div>

      <CreateFuelCodeModal
        open={isCreateCodeModalOpen}
        onCancel={handleCreateCodeCancel}
        onSubmit={handleCreateCodeSubmit}
        userPhone={normalizePhoneNumber(user.phone)}
        loading={createCodeLoading}
        error={createCodeError}
      />
      {/* {createCodeResult && (
        <div
          style={{
            color: "green",
            fontSize: 14,
            textAlign: "center",
            margin: 8,
          }}
        >
          {createCodeResult.message}
          <br />
          {createCodeResult.code && (
            <>
              מספר תדלוק: <b>{createCodeResult.code}</b>
              <br />
              סכום: <b>{createCodeResult.codeValue} ש"ח</b>
            </>
          )}
        </div>
      )} */}

      <div className={styles.editItemContainer}>
        <button
          onClick={handleClearMessages}
          disabled={isClearingMessages}
          className={styles.clearMessagesButton}
        >
          נקה היסוטריית הודעות
        </button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        message="האם אתה בטוח שברצונך לנקות את היסטוריית ההודעות?"
      />
      {editNameError && (
        <div style={{ color: "red", fontSize: 12 }}>{editNameError}</div>
      )}
      {chatTypeError && (
        <div style={{ color: "red", fontSize: 12 }}>{chatTypeError}</div>
      )}
    </div>
  );
};

export default EditUser;
